import { UNDERLINE_STATIC } from '@/components/ui/link-underline';
import { cn } from '@/lib/utils';
import { useCallback } from 'react';
import { Link } from '@tanstack/react-router';
import { Dialog } from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import { useTranslation } from '../../../hooks/useTranslation';
import { useCreateOrderSource } from '@hooks/orderSources';
import { CreateOrderSourceForm } from './CreateOrderSourceForm';
import type { OrderSource } from '@api/orderSources';
import type { CreateOrderSourceFormData } from '../../../schemas/orderSource.schema';

const TOAST_DURATION = 10_000;

interface CreateOrderSourceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (orderSource: OrderSource) => void;
}

function CreateOrderSourceDialogContent({
  onOpenChange,
  onSuccess,
}: {
  onOpenChange: (open: boolean) => void;
  onSuccess?: (orderSource: OrderSource) => void;
}) {
  const { t } = useTranslation();

  const closeDialog = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const createOrderSource = useCreateOrderSource({
    onSuccess: (orderSource) => {
      const { dismiss } = toast({
        description: (
          <span className="flex min-w-0 items-center gap-1">
            <Link
              to="/order_sources/$id"
              params={{ id: String(orderSource.id) }}
              className={cn('truncate', UNDERLINE_STATIC)}
              onClick={() => dismiss()}
            >
              {orderSource.name}
            </Link>
            <span className="shrink-0">
              {t('create_modal.create_form.saved_label', 'created')}
            </span>
          </span>
        ),
        duration: TOAST_DURATION,
      });
      onSuccess?.(orderSource);
      closeDialog();
    },
  });

  // User-initiated close paths (X button, Esc, backdrop) are gated on
  // isPending so a mid-submit close doesn't unmount the dialog while the
  // request is in flight — the source would still be created server-side
  // but the user would believe they cancelled. The success path uses
  // closeDialog directly to bypass this gate.
  const handleUserClose = useCallback(() => {
    if (createOrderSource.isPending) return;
    closeDialog();
  }, [createOrderSource.isPending, closeDialog]);

  const handleSubmit = useCallback(
    async (values: CreateOrderSourceFormData) => {
      try {
        await createOrderSource.mutateAsync({
          name: values.name,
          description: values.description ?? null,
          fee: values.fee,
          fee_type: values.fee_type,
        });
      } catch {
        // Error toast is surfaced by useCreateOrderSource's onError; swallow
        // here so the rejection doesn't bubble out of the form's submit handler.
      }
    },
    [createOrderSource],
  );

  return (
    <CreateOrderSourceForm
      isPending={createOrderSource.isPending}
      onSubmit={handleSubmit}
      onClose={handleUserClose}
    />
  );
}

export function CreateOrderSourceDialog({
  open,
  onOpenChange,
  onSuccess,
}: CreateOrderSourceDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && (
        <CreateOrderSourceDialogContent
          onOpenChange={onOpenChange}
          onSuccess={onSuccess}
        />
      )}
    </Dialog>
  );
}
